import React, { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react';
import { useToast } from '@/core/components/ToastProvider/ToastProvider';
import { fetchActivePages, Page } from '@/core/services/PageService';

interface PagesContextValue {
  pages: Page[];
  isLoading: boolean;
  error: Error | null;
}

const PagesContext = createContext<PagesContextValue | undefined>(undefined);

interface PagesProviderProps {
  children: ReactNode;
  channel: string;
}

export const PagesProvider: React.FC<PagesProviderProps> = ({ children, channel }) => {
  const { showToast } = useToast();
  const [pages, setPages] = useState<Page[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const loadPages = async () => {
      try {
        setIsLoading(true);
        const fetchedPages = await fetchActivePages(channel);
        setPages(fetchedPages || []);
      } catch (err) {
        setError(err as Error);
        console.error('Error loading pages:', err);
        showToast({
          variant: 'error',
          title: 'Erro ao carregar páginas',
          text: 'Ocorreu um erro ao carregar as páginas. Tente novamente.',
          icon: 'error',
        });
      } finally {
        setIsLoading(false);
      }
    };
    loadPages();
  }, [channel, showToast]);

  const value = useMemo(() => ({ pages, isLoading, error }), [pages, isLoading, error]);

  return <PagesContext.Provider value={value}>{children}</PagesContext.Provider>;
};

export const usePages = (): PagesContextValue => {
  const context = useContext(PagesContext);
  if (context === undefined) {
    throw new Error('usePages must be used within a PagesProvider');
  }
  return context;
};
